import React, { useState, useEffect } from 'react';
import { Container, Badge, Button, Form, InputGroup, Row, Col, Alert, Card, Table, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { taskService } from '../../services/api';
import './TaskList.css';

const TaskList = () => {
  const { isAdmin, currentUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all'
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        const response = await taskService.getAllTasks();
        const tasksData = response.data?.data || [];
        setTasks(tasksData);
        setFilteredTasks(tasksData);
        setError(null);
      } catch (err) {
        console.error('Error fetching tasks:', err);
        setError('Failed to fetch tasks. Please try again later.');
        setTasks([]);
        setFilteredTasks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  useEffect(() => {
    // Apply filters and search
    applyFilters();
  }, [tasks, filters, searchTerm]);

  // Add this function to fix the error
  const applyFilters = () => {
    let result = [...tasks];
    
    // Apply status filter
    if (filters.status !== 'all') {
      result = result.filter(task => task.status === filters.status);
    }
    
    // Apply priority filter
    if (filters.priority !== 'all') {
      result = result.filter(task => task.priority === filters.priority);
    }
    
    // Apply search term
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      result = result.filter(task => 
        task.title.toLowerCase().includes(term) || 
        (task.description && task.description.toLowerCase().includes(term))
      );
    }
    
    setFilteredTasks(result);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Status functions
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'pending': 
      case 'todo': 
        return 'secondary';
      case 'in_progress': return 'primary';
      case 'awaiting_approval': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
      case 'todo': 
        return 'fa-circle-exclamation';
      case 'in_progress': return 'fa-spinner fa-spin';
      case 'awaiting_approval': return 'fa-clock';
      case 'completed': return 'fa-circle-check';
      case 'cancelled': return 'fa-ban';
      default: return 'fa-circle-question';
    }
  };

  // Priority functions
  const getPriorityBadgeVariant = (priority) => {
    switch (priority) {
      case 'low': return 'info';
      case 'medium': return 'warning';
      case 'high': return 'danger';
      case 'urgent': return 'danger';
      default: return 'secondary';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'low': return 'fa-arrow-down';
      case 'medium': return 'fa-equals';
      case 'high': return 'fa-arrow-up';
      case 'urgent': return 'fa-bolt';
      default: return 'fa-minus';
    }
  };

  // Format date nicely
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };

  // Get employee initials
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const handleDelete = async (taskId) => {
    try {
      await taskService.moveToTrash(taskId);
      // Refresh the task list
      const response = await taskService.getAllTasks();
      const tasksData = response.data?.data || [];
      setTasks(tasksData);
      setShowDeleteModal(false);
      setSelectedTask(null);
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task. Please try again.');
    }
  };

  const confirmDelete = (task) => {
    setSelectedTask(task);
    setShowDeleteModal(true);
  };

  if (loading) {
    return <div className="loading-container"><i className="fas fa-spinner fa-spin"></i> Loading tasks...</div>;
  }

  return (
    <Container fluid className="task-list-container py-4">
      <div className="tasks-header mb-4">
        <h2 className="page-title">
          <i className="fas fa-list-check me-2"></i>
          All Tasks
        </h2>
        <div className="header-actions">
          {/* Create Task button removed */}
        </div>
      </div>
      
      {error && <Alert variant="danger" className="mb-4">{error}</Alert>}
      
      <Card className="filter-card mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <div className="filter-group">
                <label className="filter-label">
                  <i className="fas fa-search me-2"></i>Search
                </label>
                <InputGroup>
                  <Form.Control
                    placeholder="Search tasks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {searchTerm && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => setSearchTerm('')}
                      className="clear-btn"
                    >
                      <i className="fas fa-times"></i>
                    </Button>
                  )}
                </InputGroup>
              </div>
            </Col>
            <Col md={4}>
              <div className="filter-group">
                <label className="filter-label">
                  <i className="fas fa-filter me-2"></i>Status
                </label>
                <Form.Select 
                  name="status" 
                  value={filters.status} 
                  onChange={handleFilterChange}
                  className="filter-select"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="awaiting_approval">Awaiting Approval</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </div>
            </Col>
            <Col md={4}>
              <div className="filter-group">
                <label className="filter-label">
                  <i className="fas fa-arrow-up-wide-short me-2"></i>Priority
                </label>
                <Form.Select 
                  name="priority" 
                  value={filters.priority} 
                  onChange={handleFilterChange}
                  className="filter-select"
                >
                  <option value="all">All Priorities</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </Form.Select>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <div className="task-stats mb-4">
        <div className="stat-pill">
          <div className="stat-icon"><i className="fas fa-list"></i></div>
          <span>Total: <strong>{filteredTasks.length}</strong></span>
        </div>
        
        <div className="stat-pill status-pending">
          <div className="stat-icon"><i className="fas fa-circle-exclamation"></i></div>
          <span>Pending: <strong>{filteredTasks.filter(t => t.status === 'pending' || t.status === 'todo').length}</strong></span>
        </div>
        
        <div className="stat-pill status-progress">
          <div className="stat-icon"><i className="fas fa-spinner"></i></div>
          <span>In Progress: <strong>{filteredTasks.filter(t => t.status === 'in_progress').length}</strong></span>
        </div>
        
        <div className="stat-pill status-completed">
          <div className="stat-icon"><i className="fas fa-check-circle"></i></div>
          <span>Completed: <strong>{filteredTasks.filter(t => t.status === 'completed').length}</strong></span>
        </div>
      </div>
      
      {filteredTasks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><i className="fas fa-tasks"></i></div>
          <h3>No tasks found</h3>
          <p>There are no tasks matching your filters</p>
        </div>
      ) : (
        <div className="task-table-container">
          <Table responsive className="task-table">
            <thead>
              <tr>
                <th>Task</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Department</th>
                <th>Due Date</th>
                <th>Assigned To</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map(task => (
                <tr key={task.id} className={`task-row priority-${task.priority}`}>
                  <td className="task-col">
                    <div className="task-title-cell">
                      <span className="task-title">{task.title}</span>
                      {task.description && (
                        <span className="task-description">
                          {task.description.length > 60 
                            ? `${task.description.substring(0, 60)}...` 
                            : task.description}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="status-col">
                    <Badge bg={getStatusBadgeVariant(task.status)} className="status-badge">
                      <i className={`fas ${getStatusIcon(task.status)} me-1`}></i>
                      {task.status.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="priority-col">
                    <Badge bg={getPriorityBadgeVariant(task.priority)} className="priority-badge">
                      <i className={`fas ${getPriorityIcon(task.priority)} me-1`}></i>
                      {task.priority}
                    </Badge>
                  </td>
                  <td className="dept-col">{task.department_name || '-'}</td>
                  <td className="date-col">
                    <span className="date-value">{formatDate(task.due_date)}</span>
                  </td>
                  <td className="user-col">
                    {task.assignees && task.assignees.length > 0 ? (
                      <div className="user-cell">
                        <div className="user-avatar">
                          {getInitials(task.assignees[0].username)}
                        </div>
                        <span className="user-name">
                          {task.assignees[0].username}
                        </span>
                      </div>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                  <td className="actions-col">
                    <div className="task-actions">
                      <Link 
                        to={`/tasks/${task.id}`} 
                        className="btn btn-primary btn-sm me-2"
                      >
                        <i className="fas fa-eye me-1"></i> View
                      </Link>
                      {(isAdmin || currentUser.id === task.created_by) && (
                        <Button 
                          variant="danger" 
                          size="sm"
                          onClick={() => confirmDelete(task)}
                          title="Delete Task"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Add Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to move "{selectedTask?.title}" to trash?
          <Alert variant="warning" className="mt-3">
            <small>Note: You can restore the task from the trash later if needed.</small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => handleDelete(selectedTask?.id)}>
            Move to Trash
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TaskList;
